import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  TrendingUp, RefreshCw, Loader2, DollarSign, ShoppingCart, Truck,
  AlertCircle, CheckCircle2, Copy, Search, PlusCircle,
  Settings, Layers, Eye, Download, X,
  ExternalLink, BarChart3, Zap, Edit3, FileSpreadsheet, Sparkles
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import {
  AdProfitabilityResponse, AdPerformanceRow,
  TikTokAdsConfig
} from '../../types';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import './TikTokAdsProfitReport.css';

const DATE_PRESETS = [
  { value: 'today', label: 'Today (আজকে)' },
  { value: 'yesterday', label: 'Yesterday (গতকাল)' },
  { value: 'last_3d', label: 'Last 3 Days (৩ দিন)' },
  { value: 'last_7d', label: 'Last 7 Days (৭ দিন)' },
  { value: 'last_14d', label: 'Last 14 Days (১৪ দিন)' },
  { value: 'last_30d', label: 'Last 30 Days (৩০ দিন)' },
  { value: 'this_month', label: 'This Month (এই মাস)' },
  { value: 'last_month', label: 'Last Month (গত মাস)' },
  { value: 'custom', label: 'Custom Range (নির্দিষ্ট তারিখ)' },
];

const PLATFORMS = [
  { value: 'tiktok', label: 'TikTok Ads' },
  { value: 'facebook', label: 'Facebook / Meta' },
  { value: 'all', label: 'All Platforms (সকল)' },
];

interface TikTokAdsProfitReportProps {
  defaultPlatform?: 'tiktok' | 'facebook' | 'all';
}

export const TikTokAdsProfitReport: React.FC<TikTokAdsProfitReportProps> = ({ defaultPlatform = 'tiktok' }) => {
  const [data, setData] = useState<AdProfitabilityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  // Filters
  const [platform, setPlatform] = useState<string>(defaultPlatform);
  const [datePreset, setDatePreset] = useState('last_7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & Panels
  const [selectedAdForOrders, setSelectedAdForOrders] = useState<AdPerformanceRow | null>(null);
  const [isLogSpendModalOpen, setIsLogSpendModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  
  // Toast notifications
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    if (defaultPlatform) {
      setPlatform(defaultPlatform);
    }
  }, [defaultPlatform]);

  // Log Spend Form State: 'auto' | 'single' | 'bulk'
  const [spendTab, setSpendTab] = useState<'auto' | 'single' | 'bulk'>('auto');

  // Auto-Fetch Form State (for users with Advertiser Account)
  const [autoFetchForm, setAutoFetchForm] = useState({
    platform: defaultPlatform === 'facebook' ? 'facebook' : 'tiktok',
    ad_id: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [fetchingLive, setFetchingLive] = useState(false);
  const [livePreview, setLivePreview] = useState<any>(null);
  const [savingLiveAd, setSavingLiveAd] = useState(false);

  // Single Manual Entry State (for users without API credentials)
  const [singleSpendForm, setSingleSpendForm] = useState({
    platform: defaultPlatform === 'facebook' ? 'facebook' : 'tiktok',
    ad_id: '',
    ad_name: '',
    campaign_name: '',
    date: new Date().toISOString().split('T')[0],
    spend: '',
    impressions: '',
    clicks: '',
    notes: '',
  });

  // Bulk Spend State
  const [bulkPlatform, setBulkPlatform] = useState<'tiktok' | 'facebook'>(defaultPlatform === 'facebook' ? 'facebook' : 'tiktok');
  const [bulkSpendText, setBulkSpendText] = useState('');
  const [savingSpend, setSavingSpend] = useState(false);

  // API Config State (TikTok & Meta)
  const [configTab, setConfigTab] = useState<'tiktok' | 'meta'>(defaultPlatform === 'facebook' ? 'meta' : 'tiktok');
  const [configs, setConfigs] = useState<TikTokAdsConfig[]>([]);
  const [configForm, setConfigForm] = useState<Partial<TikTokAdsConfig>>({
    advertiser_id: '',
    access_token: '',
    currency: 'USD',
    usd_to_bdt_rate: 122.0,
    is_active: true,
    auto_sync: false,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingApi, setTestingApi] = useState(false);

  // Meta API Config State
  const [metaConfigs, setMetaConfigs] = useState<any[]>([]);
  const [metaConfigForm, setMetaConfigForm] = useState({
    id: undefined as number | undefined,
    ad_account_id: '',
    access_token: '',
    app_id: '',
    app_secret: '',
    name: 'Primary Meta Ad Account',
    is_active: true,
  });
  const [savingMetaConfig, setSavingMetaConfig] = useState(false);
  const [testingMetaApi, setTestingMetaApi] = useState(false);
  const [syncingApi, setSyncingApi] = useState(false);

  // Load Analytics Data
  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      const params: any = { platform, date_preset: datePreset };
      if (datePreset === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }

      const res = await adminAPI.getAdProfitabilityAnalytics(params);
      setData(res.data || res);
    } catch (err: any) {
      console.error('Failed to load profitability analytics:', err);
      setErrorMsg(err.response?.data?.error || 'Failed to load marketing profitability data.');
    } finally {
      setLoading(false);
    }
  }, [platform, datePreset, startDate, endDate]);

  // Load TikTok Ads Config
  const loadConfigs = useCallback(async () => {
    try {
      const res = await adminAPI.getTikTokAdsConfig();
      const list = res.data || res;
      if (Array.isArray(list) && list.length > 0) {
        setConfigs(list);
        setConfigForm(list[0]);
      }
    } catch (err) {
      console.warn('Unable to load TikTok Ads configs:', err);
    }
  }, []);

  // Load Meta Ads Config
  const loadMetaConfigs = useCallback(async () => {
    try {
      const res = await adminAPI.getMetaAdsConfig();
      const list = res.data?.results || res.data || res;
      if (Array.isArray(list) && list.length > 0) {
        setMetaConfigs(list);
        setMetaConfigForm({
          id: list[0].id,
          ad_account_id: list[0].ad_account_id || '',
          access_token: list[0].access_token || '',
          app_id: list[0].app_id || '',
          app_secret: list[0].app_secret || '',
          name: list[0].name || 'Primary Meta Ad Account',
          is_active: list[0].is_active ?? true,
        });
      }
    } catch (err) {
      console.warn('Unable to load Meta Ads configs:', err);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    loadConfigs();
    loadMetaConfigs();
  }, [loadConfigs, loadMetaConfigs]);

  // Copy helper
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setToast({ show: true, message: `Copied ${label}: ${text}`, type: 'success' });
  };

  // Filter Ads rows by search query
  const filteredAds = useMemo(() => {
    if (!data?.ads_breakdown) return [];
    if (!searchQuery.trim()) return data.ads_breakdown;
    const q = searchQuery.toLowerCase().trim();
    return data.ads_breakdown.filter(
      (a) =>
        a.ad_id.toLowerCase().includes(q) ||
        (a.ad_name && a.ad_name.toLowerCase().includes(q)) ||
        (a.campaign_name && a.campaign_name.toLowerCase().includes(q)) ||
        (a.product_name && a.product_name.toLowerCase().includes(q))
    );
  }, [data, searchQuery]);

  // Save Single Ad Spend
  const handleSaveSingleSpend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!singleSpendForm.ad_id || !singleSpendForm.date) {
      setToast({ show: true, message: 'Please enter Ad ID and Date.', type: 'error' });
      return;
    }
    try {
      setSavingSpend(true);
      await adminAPI.createAdSpendLog({
        platform: singleSpendForm.platform as any,
        ad_id: singleSpendForm.ad_id.trim(),
        ad_name: singleSpendForm.ad_name.trim(),
        campaign_name: singleSpendForm.campaign_name.trim(),
        date: singleSpendForm.date,
        spend: parseFloat(singleSpendForm.spend) || 0,
        impressions: parseInt(singleSpendForm.impressions) || 0,
        clicks: parseInt(singleSpendForm.clicks) || 0,
        notes: singleSpendForm.notes,
      });
      setToast({ show: true, message: 'Daily Ad Spend saved successfully!', type: 'success' });
      setIsLogSpendModalOpen(false);
      setSingleSpendForm({
        platform: 'tiktok',
        ad_id: '',
        ad_name: '',
        campaign_name: '',
        date: new Date().toISOString().split('T')[0],
        spend: '',
        impressions: '',
        clicks: '',
        notes: '',
      });
      loadAnalytics();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save Ad Spend.', type: 'error' });
    } finally {
      setSavingSpend(false);
    }
  };

  // Bulk Save Spend (from CSV/Spreadsheet Paste)
  const handleBulkSaveSpend = async () => {
    if (!bulkSpendText.trim()) {
      setToast({ show: true, message: 'Please paste CSV or spreadsheet rows.', type: 'error' });
      return;
    }

    try {
      setSavingSpend(true);
      const lines = bulkSpendText.trim().split('\n');
      const entries: any[] = [];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        // Skip headers if user pasted header line
        if (
          i === 0 &&
          (line.toLowerCase().includes('date') || line.toLowerCase().includes('ad_id') || line.toLowerCase().includes('spend'))
        ) {
          continue;
        }

        // Support Tab separated (TSV/Excel) or Comma separated (CSV)
        const parts = line.includes('\t') ? line.split('\t') : line.split(',');
        const [rawDate, rawAdId, rawSpend, rawImpr, rawClicks, rawAdName, rawCamp] = parts.map((p) => p?.trim() || '');

        if (!rawAdId || !rawDate) continue;

        entries.push({
          platform: bulkPlatform,
          date: rawDate,
          ad_id: rawAdId,
          spend: parseFloat(rawSpend.replace(/[^0-9.]/g, '')) || 0,
          impressions: parseInt(rawImpr.replace(/[^0-9]/g, '')) || 0,
          clicks: parseInt(rawClicks.replace(/[^0-9]/g, '')) || 0,
          ad_name: rawAdName || '',
          campaign_name: rawCamp || '',
        });
      }

      if (entries.length === 0) {
        setToast({ show: true, message: 'No valid rows detected. Check format: Date, Ad_ID, Spend, Impressions, Clicks', type: 'error' });
        return;
      }

      const res = await adminAPI.bulkSaveAdSpendLogs(entries);
      setToast({
        show: true,
        message: `Successfully saved ${res.data?.saved_count || entries.length} ad spend entries!`,
        type: 'success',
      });
      setIsLogSpendModalOpen(false);
      setBulkSpendText('');
      loadAnalytics();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Bulk save failed.', type: 'error' });
    } finally {
      setSavingSpend(false);
    }
  };

  // Fetch Live Metrics for a Single Ad ID
  const handleFetchLiveAd = async (saveToDb = false) => {
    if (!autoFetchForm.ad_id.trim()) {
      setToast({ show: true, message: 'অনুগ্রহ করে Ad ID লিখুন (Please enter Ad ID)', type: 'error' });
      return;
    }

    try {
      setFetchingLive(true);
      const res = await adminAPI.fetchLiveAd({
        platform: autoFetchForm.platform,
        ad_id: autoFetchForm.ad_id.trim(),
        date: autoFetchForm.date,
        save_to_db: saveToDb,
      });

      const resData = res.data?.data || res.data;
      setLivePreview(resData);

      if (saveToDb) {
        setToast({
          show: true,
          message: `Ad ${autoFetchForm.ad_id} এর লাইভ খরচ ও পারফরম্যান্স ডাটাবেজে সংরক্ষণ করা হয়েছে!`,
          type: 'success',
        });
        loadAnalytics();
      } else {
        setToast({
          show: true,
          message: 'সরাসরি বিজ্ঞাপন অ্যাকাউন্ট থেকে লাইভ ডাটা পাওয়া গেছে!',
          type: 'success',
        });
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'লাইভ তথ্য ফেচ করা সম্ভব হয়নি। Ad ID ও API সেটিংস যাচাই করুন।';
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setFetchingLive(false);
    }
  };

  // Save Live Previewed Ad to Database
  const handleSaveLivePreview = async () => {
    if (!livePreview) return;
    try {
      setSavingLiveAd(true);
      await adminAPI.createAdSpendLog({
        platform: livePreview.platform,
        ad_id: livePreview.ad_id,
        date: livePreview.date || autoFetchForm.date,
        spend: livePreview.spend,
        impressions: livePreview.impressions,
        clicks: livePreview.clicks,
        ad_name: livePreview.ad_name,
        campaign_name: livePreview.campaign_name,
        is_api_synced: true,
      });
      setToast({
        show: true,
        message: `Ad ${livePreview.ad_id} এর লাইভ স্পেন্ড ডাটাবেজে সংরক্ষণ সম্পন্ন হয়েছে!`,
        type: 'success',
      });
      setLivePreview({ ...livePreview, saved_to_db: true });
      loadAnalytics();
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.error || 'লাইভ স্পেন্ড সেভ করতে ব্যর্থ হয়েছে।',
        type: 'error',
      });
    } finally {
      setSavingLiveAd(false);
    }
  };

  // Save TikTok Ads Config
  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!configForm.advertiser_id || !configForm.access_token) {
      setToast({ show: true, message: 'Advertiser ID and Access Token are required.', type: 'error' });
      return;
    }

    try {
      setSavingConfig(true);
      if (configForm.id) {
        await adminAPI.updateTikTokAdsConfig(configForm.id, configForm);
      } else {
        await adminAPI.createTikTokAdsConfig(configForm);
      }
      setToast({ show: true, message: 'TikTok Ads settings saved successfully!', type: 'success' });
      setIsConfigModalOpen(false);
      loadConfigs();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save config.', type: 'error' });
    } finally {
      setSavingConfig(false);
    }
  };

  // Test TikTok Connection
  const handleTestConnection = async () => {
    if (!configForm.id) {
      setToast({ show: true, message: 'Please save configuration first.', type: 'error' });
      return;
    }
    try {
      setTestingApi(true);
      const res = await adminAPI.testTikTokAdsConnection(configForm.id);
      setToast({
        show: true,
        message: res.data?.message || 'Connection to TikTok Marketing API verified!',
        type: 'success',
      });
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.message || 'Connection test failed. Check Advertiser ID & Token.',
        type: 'error',
      });
    } finally {
      setTestingApi(false);
    }
  };

  // Save Meta Ads Config
  const handleSaveMetaConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!metaConfigForm.ad_account_id || !metaConfigForm.access_token) {
      setToast({ show: true, message: 'Meta Ad Account ID and Access Token are required.', type: 'error' });
      return;
    }
    try {
      setSavingMetaConfig(true);
      if (metaConfigForm.id) {
        await adminAPI.updateMetaAdsConfig(metaConfigForm.id, metaConfigForm);
      } else {
        await adminAPI.createMetaAdsConfig(metaConfigForm);
      }
      setToast({ show: true, message: 'Meta / Facebook Ads settings saved successfully!', type: 'success' });
      setIsConfigModalOpen(false);
      loadMetaConfigs();
    } catch (err: any) {
      setToast({ show: true, message: err.response?.data?.error || 'Failed to save Meta config.', type: 'error' });
    } finally {
      setSavingMetaConfig(false);
    }
  };

  // Test Meta Connection
  const handleTestMetaConnection = async () => {
    if (!metaConfigForm.id) {
      setToast({ show: true, message: 'Please save Meta configuration first.', type: 'error' });
      return;
    }
    try {
      setTestingMetaApi(true);
      const res = await adminAPI.testMetaAdsConnection(metaConfigForm.id);
      setToast({
        show: true,
        message: res.data?.message || 'Connection to Meta Marketing API verified!',
        type: 'success',
      });
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.message || 'Connection test failed. Check Ad Account ID & Token.',
        type: 'error',
      });
    } finally {
      setTestingMetaApi(false);
    }
  };

  // Unified Sync for TikTok, Meta, or All
  const handleSyncAds = async () => {
    try {
      setSyncingApi(true);
      const res = await adminAPI.syncPlatformAds({
        platform,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });

      const resData = res.data || res;
      let syncMsg = 'বিজ্ঞাপন একাউন্ট সফলভাবে সিঙ্ক হয়েছে!';
      if (resData.results) {
        const ttCount = resData.results.tiktok?.synced_count ?? 0;
        const fbCount = resData.results.facebook?.synced_ads ?? 0;
        syncMsg = `সিঙ্ক সম্পন্ন: TikTok (${ttCount} টি), Meta (${fbCount} টি) Ad আপডেট হয়েছে!`;
      }

      setToast({
        show: true,
        message: syncMsg,
        type: 'success',
      });
      loadAnalytics();
    } catch (err: any) {
      setToast({
        show: true,
        message: err.response?.data?.error || 'Ads Sync failed. Check API credentials.',
        type: 'error',
      });
    } finally {
      setSyncingApi(false);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    if (!filteredAds.length) return;
    const headers = [
      'Ad ID',
      'Ad Name',
      'Campaign',
      'Spend (BDT)',
      'Impressions',
      'Clicks',
      'CTR%',
      'Total Orders',
      'Delivered Orders',
      'Conversion Rate %',
      'CPA (BDT)',
      'Delivered Revenue (BDT)',
      'COGS (BDT)',
      'Courier Cost (BDT)',
      'True Net Profit (BDT)',
      'Margin %',
      'ROAS',
      'POAS',
    ];

    const rows = filteredAds.map((a) => [
      `"${a.ad_id}"`,
      `"${a.ad_name || ''}"`,
      `"${a.campaign_name || ''}"`,
      a.spend.toFixed(2),
      a.impressions,
      a.clicks,
      `${a.ctr.toFixed(2)}%`,
      a.orders_count,
      a.delivered_count,
      `${a.conversion_rate.toFixed(2)}%`,
      a.cpa.toFixed(2),
      a.gross_revenue.toFixed(2),
      a.cogs.toFixed(2),
      a.courier_expense.toFixed(2),
      a.true_net_profit.toFixed(2),
      `${a.margin_percentage.toFixed(1)}%`,
      a.roas.toFixed(2),
      a.poas.toFixed(2),
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${platform}_ad_profitability_${datePreset}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const summary = data?.summary;
  const isNetProfitPositive = (summary?.true_net_profit || 0) >= 0;

  return (
    <div className="admin-page adm-page ad-profit-page">
      {/* Top Header */}
      <div className="adm-topbar">
        <div>
          <h1 className="adm-page-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span>
              {platform === 'facebook'
                ? 'Meta / Facebook Ads'
                : platform === 'all'
                ? 'All Marketing & Ads (TikTok + Facebook)'
                : 'TikTok Ads'}{' '}
              & Profitability
            </span>
            <span className="profit-badge profit-badge-purple">Live Financials</span>
          </h1>
          <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 13 }}>
            Track {platform === 'facebook' ? 'Facebook / Instagram' : platform === 'all' ? 'TikTok & Facebook' : 'TikTok'} Ad ID spend, real conversions, delivery courier loss, and True Net Profit.
          </p>
        </div>

        <div className="adm-topbar-right">
          <button className="profit-btn profit-btn-outline" onClick={() => setIsConfigModalOpen(true)}>
            <Settings size={15} /> API Settings
          </button>
          <button className="profit-btn profit-btn-primary" onClick={() => setIsLogSpendModalOpen(true)}>
            <PlusCircle size={15} /> Log / Fetch Ad Spend
          </button>
          <button className="profit-btn profit-btn-sync" onClick={handleSyncAds} disabled={syncingApi}>
            {syncingApi ? <Loader2 size={15} className="adm-spin" /> : <Zap size={15} />}
            {syncingApi
              ? 'Syncing...'
              : platform === 'facebook'
              ? 'Sync Meta API'
              : platform === 'all'
              ? 'Sync All APIs'
              : 'Sync TikTok API'}
          </button>
        </div>
      </div>

      {/* Formula & Explanation Banner */}
      <div className="profit-formula-banner">
        <div className="profit-formula-text">
          <span className="profit-formula-badge">হিসাবের সূত্র</span>
          <span>
            <strong>প্রকৃত নিট লাভ (True Net Profit)</strong> = ডেলিভারি রাজস্ব (Delivered Rev) − পণ্য কেনা খরচ (COGS) − বিজ্ঞাপন খরচ (Ad Spend) − কুরিয়ার চার্জ ও রিটার্ন ক্ষতি (Net Courier)
          </span>
        </div>
        <div className="profit-formula-code">
          Net Profit = Revenue − COGS − Ad Spend − Net Courier Loss
        </div>
      </div>

      {/* Error Alert */}
      {errorMsg && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 18px', marginBottom: 20, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertCircle size={18} />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Filter Toolbar */}
      <div className="profit-toolbar">
        <div className="profit-toolbar-left">
          {/* Platform Selector */}
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="profit-select">
            {PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          {/* Date Presets */}
          <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className="profit-select">
            {DATE_PRESETS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>

          {/* Custom Date Pickers */}
          {datePreset === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="profit-date-input"
              />
              <span style={{ color: '#94a3b8' }}>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="profit-date-input"
              />
            </div>
          )}

          <button className="profit-btn profit-btn-outline" onClick={loadAnalytics} disabled={loading}>
            {loading ? <Loader2 size={14} className="adm-spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>

        <div className="profit-toolbar-right">
          {/* Search Filter */}
          <div className="profit-search-wrapper">
            <Search size={15} className="profit-search-icon" />
            <input
              type="text"
              placeholder="Search Ad ID, Ad Name, Campaign..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="profit-search-input"
            />
          </div>

          <button className="profit-btn profit-btn-outline" onClick={handleExportCSV} title="Export Report CSV">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="profit-kpi-grid">
        {/* HERO: True Net Profit Card */}
        <div className={`profit-kpi-card hero-profit ${!isNetProfitPositive ? 'negative' : ''}`}>
          <div className="profit-kpi-header">
            <div>
              <div className="profit-kpi-title">প্রকৃত নিট লাভ (True Net Profit)</div>
              <div className="profit-kpi-subtitle">সকল খরচ বাদ দিয়ে বাস্তব পকেটের লাভ</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: 8, borderRadius: 10 }}>
              <TrendingUp size={24} color="#ffffff" />
            </div>
          </div>
          <div className="profit-kpi-value">
            ৳{summary ? summary.true_net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>মার্জিন: <strong>{summary ? summary.margin_percentage : 0}%</strong></span>
            <span>POAS (Profit on Ad Spend): <strong>{summary ? summary.poas : 0}x</strong></span>
          </div>
        </div>

        {/* Total Ad Spend */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div>
              <div className="profit-kpi-title">মোট বিজ্ঞাপন খরচ (Ad Spend)</div>
              <div className="profit-kpi-subtitle">TikTok ও পেইড এডস খরচ</div>
            </div>
            <div style={{ color: '#7c3aed' }}>
              <DollarSign size={20} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#7c3aed' }}>
            ৳{summary ? summary.total_spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>CPA (খরচ/ডেলিভারি): <strong>৳{summary ? summary.cpa.toFixed(2) : '0.00'}</strong></span>
            <span>ROAS: <strong>{summary ? summary.roas : 0}x</strong></span>
          </div>
        </div>

        {/* Delivered Revenue */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div>
              <div className="profit-kpi-title">ডেলিভারি বিক্রয় (Delivered Rev)</div>
              <div className="profit-kpi-subtitle">সফল ডেলিভারির মোট টাকা</div>
            </div>
            <div style={{ color: '#0369a1' }}>
              <ShoppingCart size={20} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#0369a1' }}>
            ৳{summary ? summary.gross_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>ডেলিভার্ড অর্ডার: <strong>{summary ? summary.total_delivered_orders : 0} টি</strong></span>
            <span>মোট অর্ডার: <strong>{summary ? summary.total_orders_placed : 0} টি</strong></span>
          </div>
        </div>

        {/* Product Buying Cost (COGS) */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div>
              <div className="profit-kpi-title">পণ্য কেনা খরচ (COGS)</div>
              <div className="profit-kpi-subtitle">বিক্রিত পণ্যের আসল কেনা দাম</div>
            </div>
            <div style={{ color: '#d97706' }}>
              <Layers size={20} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#d97706' }}>
            ৳{summary ? summary.cogs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>গ্রস মুনাফা: <strong>৳{summary ? (summary.gross_revenue - summary.cogs).toFixed(2) : '0.00'}</strong></span>
          </div>
        </div>

        {/* Net Courier & Return Loss */}
        <div className="profit-kpi-card">
          <div className="profit-kpi-header">
            <div>
              <div className="profit-kpi-title">কুরিয়ার ক্ষতি ও খরচ (Courier Net)</div>
              <div className="profit-kpi-subtitle">ডেলিভারি + রিটার্ন পার্সেল জরিমানা</div>
            </div>
            <div style={{ color: '#dc2626' }}>
              <Truck size={20} />
            </div>
          </div>
          <div className="profit-kpi-value" style={{ color: '#dc2626' }}>
            ৳{summary ? summary.courier_expense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
          </div>
          <div className="profit-kpi-footer">
            <span>বাতিল/রিটার্ন: <strong>{summary ? summary.total_cancelled_orders : 0} টি</strong></span>
          </div>
        </div>
      </div>

      {/* Secondary Stats Bar (Traffic & Funnel KPIs) */}
      <div className="profit-secondary-stats">
        <div className="profit-stat-item">
          <span>ইম্প্রেশন (Impressions):</span>
          <strong>{summary ? summary.total_impressions.toLocaleString() : 0}</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>ক্লিক (Clicks):</span>
          <strong>{summary ? summary.total_clicks.toLocaleString() : 0}</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>ক্লিক রেট (CTR%):</span>
          <strong>{summary ? summary.total_ctr.toFixed(2) : 0}%</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>অর্ডার কনভার্সন (CR%):</span>
          <strong>{summary ? summary.conversion_rate.toFixed(2) : 0}%</strong>
        </div>
        <span className="profit-stat-sep">•</span>
        <div className="profit-stat-item">
          <span>তারিখের ব্যাপ্তি:</span>
          <strong>{data?.date_range ? `${data.date_range.start_date} থেকে ${data.date_range.end_date}` : '...'}</strong>
        </div>
      </div>

      {/* Main Per-Ad Breakdown Table */}
      <div className="profit-table-card">
        <div className="profit-table-header">
          <div className="profit-table-title">
            <BarChart3 size={18} />
            <span>Ad ID ভিত্তিক পারফরম্যান্স ও নিট লাভ ({filteredAds.length} টি Ad ID)</span>
          </div>
          <div style={{ fontSize: 13, color: '#64748b' }}>
            যেকোনো Ad ID এর অর্ডারের বিস্তারিত দেখতে "View Orders" ক্লিক করুন
          </div>
        </div>

        <div className="profit-table-wrapper">
          <table className="profit-table">
            <thead>
              <tr>
                <th>Ad ID / নাম</th>
                <th>ক্যাম্পেইন</th>
                <th style={{ textAlign: 'right' }}>খরচ (Spend)</th>
                <th style={{ textAlign: 'center' }}>ইম্প্রেশন / ক্লিক</th>
                <th style={{ textAlign: 'center' }}>CR% (কনভার্সন)</th>
                <th style={{ textAlign: 'center' }}>অর্ডার (ডেলিভার্ড / মোট)</th>
                <th style={{ textAlign: 'right' }}>CPA (ডেলিভারি খরচ)</th>
                <th style={{ textAlign: 'right' }}>বিক্রি (Revenue)</th>
                <th style={{ textAlign: 'right' }}>কেনা খরচ (COGS)</th>
                <th style={{ textAlign: 'right' }}>কুরিয়ার খরচ</th>
                <th style={{ textAlign: 'right' }}>প্রকৃত নিট লাভ</th>
                <th style={{ textAlign: 'center' }}>ROAS / POAS</th>
                <th style={{ textAlign: 'center' }}>অ্যাকশন</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
                    <Loader2 size={24} className="adm-spin" style={{ margin: '0 auto 8px auto' }} />
                    লোড হচ্ছে ডেটা... অনুগ্রহ করে অপেক্ষা করুন
                  </td>
                </tr>
              )}

              {!loading && filteredAds.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: '50px 0', color: '#64748b' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                    এই সময়ের মধ্যে কোনো Ad ID এর স্পেন্ড বা অর্ডার পাওয়া যায়নি।
                    <div style={{ marginTop: 12 }}>
                      <button className="profit-btn profit-btn-primary" onClick={() => setIsLogSpendModalOpen(true)}>
                        <PlusCircle size={14} /> Ad Spend এন্ট্রি করুন
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {!loading &&
                filteredAds.map((ad) => {
                  const isProfitable = ad.true_net_profit >= 0;
                  return (
                    <tr key={ad.ad_id}>
                      {/* Ad ID and Name */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{ad.ad_name || `Ad ${ad.ad_id}`}</span>
                            {ad.is_api_synced ? (
                              <span
                                className="profit-badge profit-badge-purple"
                                style={{ fontSize: 10, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                title="বিজ্ঞাপন একাউন্ট থেকে অটোমেটিক API ফেচ করা"
                              >
                                <Zap size={10} /> Auto API
                              </span>
                            ) : (
                              <span
                                className="profit-badge profit-badge-gray"
                                style={{ fontSize: 10, padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: 3 }}
                                title="ম্যানুয়ালি বা এক্সেল থেকে এন্ট্রি করা"
                              >
                                <Edit3 size={10} /> Manual
                              </span>
                            )}
                            {ad.platform && (
                              <span
                                className="profit-badge profit-badge-blue"
                                style={{ fontSize: 10, padding: '2px 6px', textTransform: 'uppercase' }}
                              >
                                {ad.platform === 'facebook' ? 'Meta' : ad.platform}
                              </span>
                            )}
                          </div>
                          <span className="profit-ad-id-badge">
                            ID: {ad.ad_id}
                            <button
                              className="profit-copy-btn"
                              title="Copy Ad ID"
                              onClick={() => handleCopy(ad.ad_id, 'Ad ID')}
                            >
                              <Copy size={12} />
                            </button>
                          </span>
                        </div>
                      </td>

                      {/* Campaign / Product */}
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontSize: 13, color: '#334155' }}>{ad.campaign_name || '—'}</span>
                          {ad.product_name && (
                            <span style={{ fontSize: 11, color: '#64748b' }}>পণ্য: {ad.product_name}</span>
                          )}
                        </div>
                      </td>

                      {/* Spend */}
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>
                        ৳{ad.spend.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Impressions / Clicks (CTR) */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12 }}>
                          {ad.impressions.toLocaleString()} / <strong>{ad.clicks.toLocaleString()}</strong>
                        </div>
                        <span style={{ fontSize: 11, color: '#64748b' }}>CTR: {ad.ctr.toFixed(2)}%</span>
                      </td>

                      {/* Conversion Rate */}
                      <td style={{ textAlign: 'center' }}>
                        <span
                          className={`profit-badge ${ad.conversion_rate > 3 ? 'profit-badge-green' : 'profit-badge-gray'}`}
                        >
                          {ad.conversion_rate.toFixed(2)}%
                        </span>
                      </td>

                      {/* Orders Count */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>
                          {ad.delivered_count} ডেলিভার্ড
                        </div>
                        <div style={{ fontSize: 11, color: '#64748b' }}>
                          মোট: {ad.orders_count} (বাতিল: {ad.cancelled_count})
                        </div>
                      </td>

                      {/* CPA */}
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {ad.delivered_count > 0 ? `৳${ad.cpa.toFixed(2)}` : '—'}
                      </td>

                      {/* Revenue */}
                      <td style={{ textAlign: 'right', fontWeight: 700, color: '#0369a1' }}>
                        ৳{ad.gross_revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* COGS */}
                      <td style={{ textAlign: 'right', color: '#d97706' }}>
                        ৳{ad.cogs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* Courier Cost */}
                      <td style={{ textAlign: 'right', color: '#dc2626' }}>
                        ৳{ad.courier_expense.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>

                      {/* True Net Profit */}
                      <td style={{ textAlign: 'right' }}>
                        <span
                          className={`profit-badge ${isProfitable ? 'profit-badge-green' : 'profit-badge-red'}`}
                          style={{ fontSize: 13, padding: '5px 10px' }}
                        >
                          {isProfitable ? '+' : ''}৳{ad.true_net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                          মার্জিন: {ad.margin_percentage.toFixed(1)}%
                        </div>
                      </td>

                      {/* ROAS & POAS */}
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 12 }}>
                          ROAS: <strong>{ad.roas}x</strong>
                        </div>
                        <div style={{ fontSize: 11, color: isProfitable ? '#15803d' : '#b91c1c' }}>
                          POAS: <strong>{ad.poas}x</strong>
                        </div>
                      </td>

                      {/* Action */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="profit-btn profit-btn-outline"
                          style={{ height: 30, padding: '0 10px', fontSize: 12 }}
                          onClick={() => setSelectedAdForOrders(ad)}
                        >
                          <Eye size={13} /> Orders ({ad.orders_count})
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attributed Orders Drawer / Modal */}
      {selectedAdForOrders && (
        <div className="profit-modal-overlay" onClick={() => setSelectedAdForOrders(null)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">
                  Ad ID: {selectedAdForOrders.ad_id} এর সংশ্লিষ্ট অর্ডারসমূহ
                </h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  {selectedAdForOrders.ad_name} • মোট অর্ডার: {selectedAdForOrders.orders_count} টি
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setSelectedAdForOrders(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              {(!selectedAdForOrders.recent_orders || selectedAdForOrders.recent_orders.length === 0) ? (
                <div className="profit-empty-state">
                  <div className="profit-empty-icon">📦</div>
                  <p>এই Ad ID এর অধীনে কোনো অর্ডার পাওয়া যায়নি।</p>
                </div>
              ) : (
                <div className="profit-orders-list">
                  {selectedAdForOrders.recent_orders.map((order) => {
                    const statusClass =
                      order.status === 'delivered'
                        ? 'profit-badge-green'
                        : order.status === 'cancelled'
                        ? 'profit-badge-red'
                        : 'profit-badge-blue';

                    return (
                      <div key={order.id} className="profit-order-card">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <strong style={{ fontSize: 14, color: '#0f172a' }}>{order.order_number}</strong>
                            <span className={`profit-badge ${statusClass}`} style={{ textTransform: 'capitalize' }}>
                              {order.status}
                            </span>
                          </div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>
                            ক্রেতা: <strong>{order.customer_name}</strong> ({order.customer_phone})
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            অর্ডার তারিখ: {new Date(order.created_at).toLocaleString()}
                          </div>
                        </div>

                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0369a1' }}>
                            ৳{order.final_amount.toFixed(2)}
                          </div>
                          <div style={{ fontSize: 11, color: '#d97706' }}>
                            ক্রয় খরচ: ৳{order.total_cost.toFixed(2)}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#15803d', marginTop: 2 }}>
                            গ্রস লাভ: ৳{order.gross_profit.toFixed(2)}
                          </div>
                          <a
                            href={`/admin/orders?search=${order.order_number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: 11,
                              color: '#2563eb',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 2,
                              marginTop: 4,
                            }}
                          >
                            অর্ডার ভিউ করুন <ExternalLink size={10} />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="profit-modal-footer">
              <button className="profit-btn profit-btn-outline" onClick={() => setSelectedAdForOrders(null)}>
                বন্ধ করুন
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Ad Spend Modal */}
      {isLogSpendModalOpen && (
        <div className="profit-modal-overlay" onClick={() => setIsLogSpendModalOpen(false)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">বিজ্ঞাপন খরচ এন্ট্রি ও লাইভ সিঙ্ক (Log & Ingest Ad Spend)</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  অটোমেটিক API ফেচ (Advertiser Account) অথবা সরাসরি ম্যানুয়াল এন্ট্রি
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setIsLogSpendModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              {/* 3 Main Ingestion Tabs */}
              <div className="profit-tabs">
                <button
                  className={`profit-tab-btn ${spendTab === 'auto' ? 'active' : ''}`}
                  onClick={() => setSpendTab('auto')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Zap size={14} style={{ color: spendTab === 'auto' ? '#8b5cf6' : undefined }} />
                  ⚡ অটোমেটিক লাইভ API (Auto-Fetch)
                </button>
                <button
                  className={`profit-tab-btn ${spendTab === 'single' ? 'active' : ''}`}
                  onClick={() => setSpendTab('single')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <Edit3 size={14} style={{ color: spendTab === 'single' ? '#3b82f6' : undefined }} />
                  ✍️ একক ম্যানুয়াল এন্ট্রি (Single Manual)
                </button>
                <button
                  className={`profit-tab-btn ${spendTab === 'bulk' ? 'active' : ''}`}
                  onClick={() => setSpendTab('bulk')}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <FileSpreadsheet size={14} style={{ color: spendTab === 'bulk' ? '#10b981' : undefined }} />
                  📋 এক্সেল / CSV পেস্ট (Bulk Paste)
                </button>
              </div>

              {/* TAB 1: AUTOMATIC LIVE API AUTO-FETCH */}
              {spendTab === 'auto' && (
                <div>
                  <div className="profit-helper-box">
                    <Sparkles size={18} style={{ flexShrink: 0, marginTop: 1, color: '#10b981' }} />
                    <div>
                      <strong>অটোমেটিক লাইভ API ফেচ:</strong> আপনার যদি TikTok বা Meta-র নিজস্ব Advertiser Account থাকে, তবে কেবল Ad ID ও তারিখ দিলেই সিস্টেম সরাসরি অফিসিয়াল API থেকে রিয়েল-টাইম খরচ (Spend), ইম্প্রেশন ও ক্লিকের সঠিক ডাটা নিয়ে আসবে।
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">প্ল্যাটফর্ম (Advertising Platform)</label>
                      <select
                        className="profit-form-input"
                        value={autoFetchForm.platform}
                        onChange={(e) => {
                          setAutoFetchForm({ ...autoFetchForm, platform: e.target.value as any });
                          setLivePreview(null);
                        }}
                      >
                        <option value="tiktok">TikTok Ads Manager</option>
                        <option value="facebook">Meta / Facebook Ads Manager</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <label className="profit-form-label" style={{ margin: 0 }}>তারিখ (Date)</label>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            type="button"
                            style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4 }}
                            onClick={() => setAutoFetchForm({ ...autoFetchForm, date: new Date().toISOString().split('T')[0] })}
                          >
                            আজকে
                          </button>
                          <button
                            type="button"
                            style={{ fontSize: 11, padding: '2px 8px', cursor: 'pointer', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 4 }}
                            onClick={() => {
                              const y = new Date();
                              y.setDate(y.getDate() - 1);
                              setAutoFetchForm({ ...autoFetchForm, date: y.toISOString().split('T')[0] });
                            }}
                          >
                            গতকাল
                          </button>
                        </div>
                      </div>
                      <input
                        type="date"
                        required
                        className="profit-form-input"
                        value={autoFetchForm.date}
                        onChange={(e) => setAutoFetchForm({ ...autoFetchForm, date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Ad ID (বিজ্ঞাপন আইডি) *</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input
                        type="text"
                        required
                        placeholder="उदा: 181234567890123 বা Meta Ad ID"
                        className="profit-form-input"
                        value={autoFetchForm.ad_id}
                        onChange={(e) => setAutoFetchForm({ ...autoFetchForm, ad_id: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleFetchLiveAd(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="profit-btn profit-btn-primary"
                        onClick={() => handleFetchLiveAd(false)}
                        disabled={fetchingLive || !autoFetchForm.ad_id.trim()}
                        style={{ whiteSpace: 'nowrap', minWidth: 150 }}
                      >
                        {fetchingLive ? <Loader2 size={15} className="adm-spin" /> : <Search size={15} />}
                        {fetchingLive ? 'ফেচ হচ্ছে...' : '🔍 লাইভ ফেচ'}
                      </button>
                    </div>
                    <div className="profit-form-help">
                      বিজ্ঞাপন একাউন্টের Ads Manager থেকে এই বিজ্ঞাপনের Ad ID কপি করে দিন
                    </div>
                  </div>

                  {/* Live Preview Card */}
                  {livePreview && (
                    <div className="profit-live-card">
                      <div className="profit-live-header">
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span className="profit-live-title">{livePreview.ad_name || `Ad ${livePreview.ad_id}`}</span>
                            <span className="profit-badge profit-badge-green" style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }}></span> Live API Data
                            </span>
                          </div>
                          <span style={{ fontSize: 12, color: '#64748b' }}>
                            {livePreview.campaign_name ? `ক্যাম্পেইন: ${livePreview.campaign_name} • ` : ''}
                            প্ল্যাটফর্ম: <strong>{livePreview.platform === 'facebook' ? 'Meta' : 'TikTok'}</strong> • তারিখ: {livePreview.date || autoFetchForm.date}
                          </span>
                        </div>

                        <div>
                          {livePreview.saved_to_db ? (
                            <span className="profit-badge profit-badge-green" style={{ fontSize: 12, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <CheckCircle2 size={14} /> ডাটাবেজে সেভ হয়েছে
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="profit-btn profit-btn-primary"
                              onClick={handleSaveLivePreview}
                              disabled={savingLiveAd}
                            >
                              {savingLiveAd ? <Loader2 size={14} className="adm-spin" /> : <PlusCircle size={14} />}
                              {savingLiveAd ? 'সেভ হচ্ছে...' : '💾 ডাটাবেজে সংরক্ষণ করুন'}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="profit-live-grid">
                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">খরচ (Spend)</div>
                          <div className="profit-live-stat-value" style={{ color: '#7c3aed' }}>
                            ৳{Number(livePreview.spend || livePreview.spend_bdt || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {livePreview.spend_original !== undefined && livePreview.spend_original > 0 && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                              (${Number(livePreview.spend_original).toFixed(2)})
                            </div>
                          )}
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">ইম্প্রেশন (Views)</div>
                          <div className="profit-live-stat-value">
                            {Number(livePreview.impressions || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>বিজ্ঞাপন ভিউ</div>
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">ক্লিক (Clicks)</div>
                          <div className="profit-live-stat-value" style={{ color: '#0369a1' }}>
                            {Number(livePreview.clicks || 0).toLocaleString()}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>ওয়েবসাইট ভিজিট</div>
                        </div>

                        <div className="profit-live-stat">
                          <div className="profit-live-stat-label">CTR / CPC</div>
                          <div className="profit-live-stat-value" style={{ fontSize: 14 }}>
                            {Number(livePreview.ctr || 0).toFixed(2)}% / ৳{Number(livePreview.cpc || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>পারফরম্যান্স রেট</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 1-Click Sync Entire Account Box */}
                  <div className="profit-sync-box">
                    <div>
                      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 2 }}>
                        ⚡ সম্পূর্ণ বিজ্ঞাপন একাউন্ট সিঙ্ক করতে চান?
                      </div>
                      <div className="profit-sync-box-text">
                        একটি একটি করে না এনে সম্পূর্ণ {autoFetchForm.platform === 'facebook' ? 'Meta' : 'TikTok'} বিজ্ঞাপন একাউন্টের সকল বিজ্ঞাপনের আজকের খরচ এক ক্লিকে স্বয়ংক্রিয়ভাবে ডাটাবেজে আপডেট করুন।
                      </div>
                    </div>
                    <button
                      type="button"
                      className="profit-btn profit-btn-sync"
                      onClick={handleSyncAds}
                      disabled={syncingApi}
                    >
                      {syncingApi ? <Loader2 size={14} className="adm-spin" /> : <RefreshCw size={14} />}
                      {syncingApi ? 'সিঙ্ক হচ্ছে...' : `সম্পূর্ণ ${autoFetchForm.platform === 'facebook' ? 'Meta' : 'TikTok'} একাউন্ট সিঙ্ক`}
                    </button>
                  </div>

                  {/* Notice for merchants without API */}
                  <div className="profit-helper-box warning" style={{ marginTop: 20 }}>
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontSize: 13 }}>
                      <strong>বিজ্ঞাপন একাউন্ট বা এপিআই নেই?</strong> কোনো সমস্যা নেই! আপনি যদি এজেন্সির মাধ্যমে বুস্ট করেন বা এপিআই এক্সেস না থাকে, তবে উপরের <strong>[✍️ একক ম্যানুয়াল এন্ট্রি]</strong> বা <strong>[📋 এক্সেল / CSV পেস্ট]</strong> ট্যাবে গিয়ে সরাসরি খরচ লিখে রাখুন। লাভ-ক্ষতির হিসাব সম্পূর্ণ সঠিকভাবে গণনা করা হবে!
                    </div>
                  </div>
                </div>
              )}

              {/* TAB 2: SINGLE MANUAL ENTRY */}
              {spendTab === 'single' && (
                <form onSubmit={handleSaveSingleSpend}>
                  <div className="profit-helper-box info">
                    <Edit3 size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <strong>ম্যানুয়াল স্পেন্ড এন্ট্রি:</strong> যাদের সরাসরি কোনো এপিআই (API) টোকেন নেই, তারা এজেন্সির দেওয়া Ad ID এবং প্রতিদিনের খরচের হিসাব এখানে ম্যানুয়ালি সংরক্ষণ করতে পারেন।
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">প্ল্যাটফর্ম (Platform) *</label>
                      <select
                        className="profit-form-input"
                        value={singleSpendForm.platform}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, platform: e.target.value })}
                      >
                        <option value="tiktok">TikTok Ads</option>
                        <option value="facebook">Facebook / Meta Ads</option>
                        <option value="google">Google Ads</option>
                        <option value="other">Other Marketing</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">তারিখ (Date) *</label>
                      <input
                        type="date"
                        required
                        className="profit-form-input"
                        value={singleSpendForm.date}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, date: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Ad ID (বিজ্ঞাপন আইডি) *</label>
                      <input
                        type="text"
                        required
                        placeholder="उदा: 181234567890123 বা এজেন্সির কোড"
                        className="profit-form-input"
                        value={singleSpendForm.ad_id}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, ad_id: e.target.value })}
                      />
                      <div className="profit-form-help">এই Ad ID টি অর্ডারের সাথে স্বয়ংক্রিয়ভাবে ম্যাচ করে লাভ হিসাব করবে</div>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">বিজ্ঞাপনের নাম (Ad Name / Creative)</label>
                      <input
                        type="text"
                        placeholder="उदा: Leather Bag Video 01"
                        className="profit-form-input"
                        value={singleSpendForm.ad_name}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, ad_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">মোট বিজ্ঞাপন খরচ (Spend ৳) *</label>
                      <input
                        type="number"
                        step="0.01"
                        required
                        placeholder="0.00"
                        className="profit-form-input"
                        value={singleSpendForm.spend}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, spend: e.target.value })}
                      />
                      <div className="profit-form-help">বাংলাদেশী টাকায় (BDT) মোট খরচ লিখুন</div>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">ক্যাম্পেইনের নাম (Campaign Name)</label>
                      <input
                        type="text"
                        placeholder="उदा: Winter Sale 2026"
                        className="profit-form-input"
                        value={singleSpendForm.campaign_name}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, campaign_name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">ইম্প্রেশন (Impressions - ঐচ্ছিক)</label>
                      <input
                        type="number"
                        placeholder="0"
                        className="profit-form-input"
                        value={singleSpendForm.impressions}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, impressions: e.target.value })}
                      />
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">ক্লিক (Clicks - ঐচ্ছিক)</label>
                      <input
                        type="number"
                        placeholder="0"
                        className="profit-form-input"
                        value={singleSpendForm.clicks}
                        onChange={(e) => setSingleSpendForm({ ...singleSpendForm, clicks: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">নোট (Remarks / Agency Note)</label>
                    <textarea
                      rows={2}
                      placeholder="অতিরিক্ত তথ্য বা এজেন্সির বিল রেফারেন্স..."
                      className="profit-form-textarea"
                      value={singleSpendForm.notes}
                      onChange={(e) => setSingleSpendForm({ ...singleSpendForm, notes: e.target.value })}
                    />
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '0 -24px -24px -24px' }}>
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsLogSpendModalOpen(false)}
                    >
                      বাতিল
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingSpend}>
                      {savingSpend ? 'সংরক্ষণ হচ্ছে...' : 'ম্যানুয়াল স্পেন্ড সংরক্ষণ করুন'}
                    </button>
                  </div>
                </form>
              )}

              {/* TAB 3: BULK SPREADSHEET PASTE */}
              {spendTab === 'bulk' && (
                <div>
                  <div className="profit-helper-box info">
                    <FileSpreadsheet size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <strong>এক্সেল / গুগল শিট বাল্ক পেস্ট:</strong> এজেন্সি থেকে পাওয়া দৈনিক খরচের তালিকা কপি করে এখানে পেস্ট করুন। ফরম্যাট:
                      <br />
                      <code>Date &emsp; Ad_ID &emsp; Spend &emsp; Impressions &emsp; Clicks &emsp; Ad_Name</code>
                    </div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">প্ল্যাটফর্ম (Platform)</label>
                    <select
                      className="profit-form-input"
                      value={bulkPlatform}
                      onChange={(e) => setBulkPlatform(e.target.value as any)}
                    >
                      <option value="tiktok">TikTok Ads</option>
                      <option value="facebook">Meta / Facebook Ads</option>
                    </select>
                  </div>

                  <textarea
                    rows={8}
                    placeholder={`2026-09-30\t181234567890123\t1500.00\t12000\t240\tHandbag Ad 1\n2026-09-30\t181234567890124\t2000.00\t18000\t310\tWatch Video 2`}
                    className="profit-form-textarea"
                    style={{ fontFamily: 'monospace', fontSize: 12 }}
                    value={bulkSpendText}
                    onChange={(e) => setBulkSpendText(e.target.value)}
                  />

                  <div className="profit-modal-footer" style={{ margin: '16px -24px -24px -24px' }}>
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsLogSpendModalOpen(false)}
                    >
                      বাতিল
                    </button>
                    <button
                      type="button"
                      className="profit-btn profit-btn-primary"
                      onClick={handleBulkSaveSpend}
                      disabled={savingSpend}
                    >
                      {savingSpend ? 'আপলোড হচ্ছে...' : 'বাল্ক সেভ করুন'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Marketing APIs Configuration Modal */}
      {isConfigModalOpen && (
        <div className="profit-modal-overlay" onClick={() => setIsConfigModalOpen(false)}>
          <div className="profit-modal profit-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="profit-modal-header">
              <div>
                <h3 className="profit-modal-title">মার্কেটিং API কনফিগারেশন (Marketing APIs Config)</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  TikTok এবং Meta (Facebook) বিজ্ঞাপন একাউন্টের ক্রেডেনশিয়াল পরিচালনা করুন
                </span>
              </div>
              <button className="profit-modal-close" onClick={() => setIsConfigModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="profit-modal-body">
              <div className="profit-tabs">
                <button
                  className={`profit-tab-btn ${configTab === 'tiktok' ? 'active' : ''}`}
                  onClick={() => setConfigTab('tiktok')}
                >
                  TikTok Marketing API {configs.length > 0 ? '(কনফিগার করা ✓)' : ''}
                </button>
                <button
                  className={`profit-tab-btn ${configTab === 'meta' ? 'active' : ''}`}
                  onClick={() => setConfigTab('meta')}
                >
                  Meta (Facebook) Marketing API {metaConfigs.length > 0 ? '(কনফিগার করা ✓)' : ''}
                </button>
              </div>

              {configTab === 'tiktok' ? (
                <form onSubmit={handleSaveConfig}>
                  <div className="profit-form-group">
                    <label className="profit-form-label">Advertiser ID (বিজ্ঞাপন একাউন্ট আইডি) *</label>
                    <input
                      type="text"
                      required
                      placeholder="उदा: 7123456789012345678"
                      className="profit-form-input"
                      value={configForm.advertiser_id || ''}
                      onChange={(e) => setConfigForm({ ...configForm, advertiser_id: e.target.value })}
                    />
                    <div className="profit-form-help">TikTok Ads Manager এ দেখতে পাবেন (Advertiser ID)</div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Marketing API Access Token (লং-টার্ম টোকেন) *</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="eyJhbGciOi..."
                      className="profit-form-textarea"
                      value={configForm.access_token || ''}
                      onChange={(e) => setConfigForm({ ...configForm, access_token: e.target.value })}
                    />
                    <div className="profit-form-help">TikTok Business Center / Developer Portal থেকে সংগৃহীত টোকেন</div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">কারেন্সি (Ad Currency)</label>
                      <select
                        className="profit-form-input"
                        value={configForm.currency || 'USD'}
                        onChange={(e) => setConfigForm({ ...configForm, currency: e.target.value as any })}
                      >
                        <option value="USD">USD ($)</option>
                        <option value="BDT">BDT (৳)</option>
                      </select>
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">USD থেকে BDT রেট (Exchange Rate)</label>
                      <input
                        type="number"
                        step="0.1"
                        className="profit-form-input"
                        value={configForm.usd_to_bdt_rate || 122.0}
                        onChange={(e) => setConfigForm({ ...configForm, usd_to_bdt_rate: parseFloat(e.target.value) || 122.0 })}
                      />
                      <div className="profit-form-help">বিজ্ঞাপন ডলারে হলে এই রেটে টাকায় কনভার্ট হবে</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
                    <input
                      type="checkbox"
                      id="auto_sync"
                      checked={configForm.auto_sync || false}
                      onChange={(e) => setConfigForm({ ...configForm, auto_sync: e.target.checked })}
                    />
                    <label htmlFor="auto_sync" style={{ fontSize: 13, color: '#334155', cursor: 'pointer' }}>
                      প্রতিদিন স্বয়ংক্রিয়ভাবে ব্যাকগ্রাউন্ডে খরচ সিঙ্ক করুন (Auto Daily Sync)
                    </label>
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '20px -24px -24px -24px' }}>
                    {configForm.id && (
                      <button
                        type="button"
                        className="profit-btn profit-btn-outline"
                        onClick={handleTestConnection}
                        disabled={testingApi}
                      >
                        {testingApi ? <Loader2 size={13} className="adm-spin" /> : <CheckCircle2 size={13} />}
                        {testingApi ? 'যাচাই হচ্ছে...' : 'কানেকশন টেস্ট'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsConfigModalOpen(false)}
                    >
                      বাতিল
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingConfig}>
                      {savingConfig ? 'সংরক্ষণ হচ্ছে...' : 'TikTok সেটিংস সেভ করুন'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSaveMetaConfig}>
                  <div className="profit-form-group">
                    <label className="profit-form-label">Meta Ad Account ID (অ্যাকাউন্ট আইডি) *</label>
                    <input
                      type="text"
                      required
                      placeholder="उदा: act_1234567890123"
                      className="profit-form-input"
                      value={metaConfigForm.ad_account_id}
                      onChange={(e) => setMetaConfigForm({ ...metaConfigForm, ad_account_id: e.target.value })}
                    />
                    <div className="profit-form-help">Meta Ads Manager থেকে Ad Account ID (act_ সহ বা ছাড়া)</div>
                  </div>

                  <div className="profit-form-group">
                    <label className="profit-form-label">Meta System User / Access Token *</label>
                    <textarea
                      rows={3}
                      required
                      placeholder="EAAG..."
                      className="profit-form-textarea"
                      value={metaConfigForm.access_token}
                      onChange={(e) => setMetaConfigForm({ ...metaConfigForm, access_token: e.target.value })}
                    />
                    <div className="profit-form-help">Meta Business Manager বা Graph API Explorer এর ads_read টোকেন</div>
                  </div>

                  <div className="profit-form-row">
                    <div className="profit-form-group">
                      <label className="profit-form-label">Meta App ID (ঐচ্ছিক)</label>
                      <input
                        type="text"
                        placeholder="उदा: 9876543210"
                        className="profit-form-input"
                        value={metaConfigForm.app_id}
                        onChange={(e) => setMetaConfigForm({ ...metaConfigForm, app_id: e.target.value })}
                      />
                    </div>

                    <div className="profit-form-group">
                      <label className="profit-form-label">কনফিগারেশন নাম</label>
                      <input
                        type="text"
                        className="profit-form-input"
                        value={metaConfigForm.name}
                        onChange={(e) => setMetaConfigForm({ ...metaConfigForm, name: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="profit-modal-footer" style={{ margin: '20px -24px -24px -24px' }}>
                    {metaConfigForm.id && (
                      <button
                        type="button"
                        className="profit-btn profit-btn-outline"
                        onClick={handleTestMetaConnection}
                        disabled={testingMetaApi}
                      >
                        {testingMetaApi ? <Loader2 size={13} className="adm-spin" /> : <CheckCircle2 size={13} />}
                        {testingMetaApi ? 'যাচাই হচ্ছে...' : 'Meta কানেকশন টেস্ট'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="profit-btn profit-btn-outline"
                      onClick={() => setIsConfigModalOpen(false)}
                    >
                      বাতিল
                    </button>
                    <button type="submit" className="profit-btn profit-btn-primary" disabled={savingMetaConfig}>
                      {savingMetaConfig ? 'সংরক্ষণ হচ্ছে...' : 'Meta সেটিংস সেভ করুন'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </div>
  );
};

export default TikTokAdsProfitReport;
